import type { DisplayFile } from './types'

/**
 * 格式化文件大小（通用版，保留2位小数）
 */
export function formatFileSize(bytes: number): string {
  // 边界处理：非数字/负数
  if (typeof bytes !== 'number' || bytes < 0) return '0 B'
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(k)),
    sizes.length - 1
  )
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 获取文件图标类型
 */
export function getFileIcon(fileName: string): string {
  if (!fileName) return 'wenjian' // 空文件名边界处理

  const ext = fileName.split('.').pop()?.toLowerCase() || ''

  const iconMap: Record<string, string> = {
    // 文档
    pdf: 'pdf',
    doc: 'word',
    docx: 'word',
    xls: 'excel',
    xlsx: 'excel',
    ppt: 'ppt',
    pptx: 'ppt',
    txt: 'txt',
    md: 'md',

    // 图片
    jpg: 'tupian',
    jpeg: 'tupian',
    png: 'tupian',
    gif: 'tupian',
    bmp: 'tupian',
    webp: 'tupian',
    svg: 'tupian',
    ico: 'tupian', // 补充ico图标类型

    // 压缩文件
    zip: 'yasuo',
    rar: 'yasuo',
    '7z': 'yasuo',
    tar: 'yasuo',
    gz: 'yasuo',
    zipx: 'yasuo', // 补充少见的压缩格式

    // 其他
    exe: 'exe',
    dmg: 'dmg',
    iso: 'iso',
    mp4: 'video', // 补充视频类型
    mp3: 'audio' // 补充音频类型
  }

  return iconMap[ext] || 'wenjian'
}

/**
 * 判断是否是图片文件
 */
export function isImageFile(fileName: string): boolean {
  if (!fileName) return false // 边界处理
  const imageExtensions = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'webp',
    'svg',
    'ico'
  ]
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  return imageExtensions.includes(ext)
}

/**
 * 判断是否是PDF文件
 */
export function isPDFFile(fileName: string): boolean {
  if (!fileName) return false
  return fileName.toLowerCase().endsWith('.pdf')
}

/**
 * 判断是否是文本文件
 */
export function isTextFile(fileName: string): boolean {
  if (!fileName) return false
  const textExtensions = [
    'txt',
    'md',
    'json',
    'xml',
    'html',
    'htm',
    'js',
    'ts',
    'css',
    'vue',
    'jsx',
    'tsx'
  ]
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  return textExtensions.includes(ext)
}

/**
 * 增强版：获取文件类型描述（更友好的中文提示）
 */
export function getFileTypeDescription(accept?: string): string {
  if (!accept) return '常见文件格式'

  const typeMap: Record<string, string> = {
    'image/*': '图片',
    '.jpg': 'JPG图片',
    '.jpeg': 'JPEG图片',
    '.png': 'PNG图片',
    '.gif': 'GIF图片',
    '.webp': 'WEBP图片',
    '.pdf': 'PDF文档',
    '.doc': 'Word文档',
    '.docx': 'Word文档',
    '.xls': 'Excel表格',
    '.xlsx': 'Excel表格',
    '.ppt': 'PPT演示文稿',
    '.pptx': 'PPT演示文稿',
    'text/*': '文本文件',
    '.txt': 'TXT文本',
    '.md': 'Markdown文件',
    '.zip': 'ZIP压缩包',
    '.rar': 'RAR压缩包',
    '.7z': '7Z压缩包'
  }

  const types = accept
    .split(',')
    .map((type) => {
      const trimmed = type.trim()
      // 优先匹配预设的友好名称
      if (typeMap[trimmed]) return typeMap[trimmed]
      // 通配符兜底（如 video/* → 视频）
      if (trimmed.includes('/*')) {
        const mainType = trimmed.split('/')[0]
        const mainTypeMap: Record<string, string> = {
          image: '图片',
          video: '视频',
          audio: '音频',
          text: '文本'
        }
        return mainTypeMap[mainType] || trimmed
      }
      // 其他情况格式化（如 png → PNG格式）
      return trimmed.replace('.', '').toUpperCase() + '格式'
    })
    // 去重 + 过滤空值
    .filter((item, index, arr) => item && arr.indexOf(item) === index)

  // 处理多个类型的拼接（最后一个用“或”，前面用顿号）
  if (types.length > 1) {
    const last = types.pop()
    return `${types.join('、')}或${last}`
  }
  return types[0] || '常见文件格式'
}

/**
 * 格式化文件大小限制文本（用于提示，保留1位小数，更简洁）
 */
export function formatMaxFileSize(size: number | undefined | null): string {
  // 边界处理：无限制的情况
  if (
    size === 0 ||
    size === Infinity ||
    size === undefined ||
    size === null ||
    isNaN(size)
  ) {
    return '无限制'
  }
  // 小于1KB → 显示B
  if (size < 1024) return `${size}B`
  // 小于1MB → 显示KB
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`
  // 小于1GB → 显示MB
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)}MB`
  // 大于等于1GB → 显示GB
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)}GB`
}

/**
 * 转换 UploadFileInfo 为 DisplayFile（增强类型安全）
 */
export function convertToDisplayFile(file: any): DisplayFile {
  // 边界处理：空文件对象
  if (!file) {
    return {
      id: '',
      name: '',
      url: '',
      thumbUrl: '',
      size: 0,
      status: 'error',
      type: ''
    }
  }
  return {
    id: file.id?.toString() || '',
    name: file.name || '未知文件',
    url: file.url || '',
    thumbUrl: file.thumbUrl || file.url || '',
    size: file.size || file.file?.size || 0,
    status: file.status || 'pending',
    type: file.type || file.file?.type || ''
  }
}
